import React, { Fragment, useState } from "react";
import { Trans } from "../utils/i18n";
import { useFormState } from "../FormComponents/FormState";
import classNames from "classnames";
import { useQuery } from "@tanstack/react-query";
import { axiosClient } from "../utils/axiosClient";
import { uniqBy } from "lodash";
import { usePage } from "@inertiajs/inertia-react";
import {getFlagEmoji} from "../utils/getFlagEmoji";
import {trackConversion} from "../utils/analytics";

const getSelfRepairLanguages = (selfRepairGuidesData) => {
    return uniqBy(
        selfRepairGuidesData?.data?.data
            ?.flatMap((selfRepairGuide) =>
                selfRepairGuide.files?.map((file) => file.language)
            )
            ?.filter(Boolean) ?? // Some files may not have language
            [],
        (language) => language.code
    );
}

export const SelfRepairGuidesList = React.memo(
    function ({ values, form, columns = 3, showOtherAvailableDocuments }) {
        const { selectedCategory } = useFormState();
        const {
            props: { currentLanguage },
        } = usePage();
        const [
            selectedSelfRepairGuideLanguageCode,
            setSelectedSelfRepairGuideLanguageCode,
        ] = useState(currentLanguage);
        const { data: selfRepairGuidesData } = useQuery({
            queryKey: [selectedCategory?.id, "selfRepairGuides"],
            queryFn: async () => {
                const response = await axiosClient.get(
                    route("selfrepair.getFlows", {
                        product_id: selectedCategory?.id,
                    })
                );

                const selfRepairLanguages = getSelfRepairLanguages(response) ?? [];
                const selectedSelfRepairGuideLanguageCodeExistsInSelfRepairGuides = selfRepairLanguages.some(
                    (language) => language.code === selectedSelfRepairGuideLanguageCode
                );

                if (!selectedSelfRepairGuideLanguageCodeExistsInSelfRepairGuides) {
                    setSelectedSelfRepairGuideLanguageCode("");
                }

                return response;
            },
            enabled: !!selectedCategory?.id,
        });

        if (!selfRepairGuidesData?.data?.data) {
            return null;
        }

        const filteredSelfRepairGuides =
            selfRepairGuidesData?.data?.data
                ?.filter((selfRepairGuide) => {
                    // If no language is selected, show all self repair guides
                    if (!selectedSelfRepairGuideLanguageCode) {
                        return true;
                    }

                    // If language is selected, show only self repair guides that have files in that language
                    return selfRepairGuide.files?.some(
                        (file) =>
                            file.language?.code ===
                            selectedSelfRepairGuideLanguageCode
                    );
                })
                ?.filter(
                    (selfRepairGuide) => selfRepairGuide.files.length > 0
                ) ?? [];

        const selfRepairGuidesAttachedToProduct =
            filteredSelfRepairGuides?.filter((selfRepairGuide) => {
                const isAttachedToProduct =
                    selfRepairGuide.products?.find(
                        (product) => product.id === selectedCategory?.id
                    ) || selfRepairGuide.product?.id === selectedCategory?.id;
                return isAttachedToProduct;
            }) ?? [];

        const selfRepairGuidesOther =
            uniqBy(filteredSelfRepairGuides?.filter((selfRepairGuide) => {
                const isAttachedToProduct =
                    selfRepairGuide.products?.find(
                        (product) => product.id === selectedCategory?.id
                    ) || selfRepairGuide.product?.id === selectedCategory?.id;

                const sameNameIsAttachedToProduct = selfRepairGuidesAttachedToProduct.some(sameNameSelfRepairGuide => {
                    return sameNameSelfRepairGuide.name === selfRepairGuide.name;
                })

                return !isAttachedToProduct && !sameNameIsAttachedToProduct;
            }) ?? [], 'name');

        const selfRepairLanguages = getSelfRepairLanguages(selfRepairGuidesData);

        function renderSelfRepairGuides(guides = []) {
            return (
                <>
                    {guides.map((selfRepairGuide) => {
                        return (
                            <Fragment key={selfRepairGuide.id}>
                                {selfRepairGuide.files
                                    .filter((file) => {
                                        if (
                                            !selectedSelfRepairGuideLanguageCode
                                        ) {
                                            return true;
                                        }

                                        return (
                                            file.language?.code ===
                                            selectedSelfRepairGuideLanguageCode
                                        );
                                    })
                                    .map((file) => (
                                        <div
                                            className={classNames({
                                                "col-span-12": true,
                                                "md:col-span-3": columns === 4,
                                                "sm:col-span-6 md:col-span-4":
                                                    columns === 3,
                                            })}
                                            key={file.id}
                                        >
                                            <div className="flex flex-col items-center justify-center w-full border-2 border-gray-200 rounded-lg overflow-hidden">
                                                <img
                                                    src={
                                                        "/images/amazon/home_download.png"
                                                    }
                                                    className={
                                                        "border-[#A4D9FB] border-b-4 w-full"
                                                    }
                                                />
                                                <div className="w-full p-5 max-w-full">
                                                    <div className="max-w-full">
                                                        <p className="text-lg font-bold text-center m-0">
                                                            {
                                                                selfRepairGuide.name
                                                            }
                                                        </p>
                                                        {file.language && (
                                                            <div className="min-w-0 w-full max-w-full mt-1">
                                                                <p className="flex justify-center items-center gap-1 text-base text-center m-0 w-full">
                                                                    <span className="truncate min-w-0">
                                                                        <span className="mr-1 shrink-0">
                                                                            {getFlagEmoji(file.language.code)}
                                                                        </span>
                                                                        {file.language.name}
                                                                    </span>
                                                                </p>
                                                            </div>
                                                        )}

                                                    </div>
                                                    <a
                                                        target="_blank"
                                                        href={file.path}
                                                        className={classNames(
                                                            "mt-2 no-underline text-center block w-full font-bold bg-secondary rounded-md text-white px-9 py-3  hover:bg-white hover:text-secondary cursor-pointer shadow-lg text-sm",
                                                            {}
                                                        )}
                                                        onClick={() => {
                                                            trackConversion({
                                                               action: "DocumentationDownload",
                                                                resourceId: file.id,
                                                                resourceType: 'App\\Models\\File',
                                                            });
                                                        }}
                                                    >
                                                        <Trans i18nKey="common.downloadPDF">
                                                            Download PDF
                                                        </Trans>
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                            </Fragment>
                        );
                    })}
                </>
            );
        }

        return (
            <div>
                <div className="flex items-center justify-between flex-wrap gap-4 mt-9">
                    <h3>
                        <Trans i18nKey="orderEntry.selfRepairGuides.title">
                            Self Repair Guides
                        </Trans>
                    </h3>
                    <div>
                        <select
                            id="language"
                            value={selectedSelfRepairGuideLanguageCode}
                            onChange={(e) =>
                                setSelectedSelfRepairGuideLanguageCode(
                                    e.target.value
                                )
                            }
                            name="language"
                            className="block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-primary-500"
                        >
                            <option value="">
                                <Trans i18nKey="orderEntry.selfRepairGuides.allLanguages">
                                    All Languages
                                </Trans>
                            </option>
                            {selfRepairLanguages?.map((language) => (
                                <option key={language.id} value={language.code}>
                                    {language.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className={"grid grid-cols-12 gap-6"}>
                    {selfRepairGuidesAttachedToProduct.length === 0 ? (
                        <div className="col-span-12">
                            <p className="m-0">
                                <Trans i18nKey="orderEntry.selfRepairGuides.noGuidesForProduct">
                                    No self repair guides available for this
                                    product.
                                </Trans>
                            </p>
                        </div>
                    ) : (
                        renderSelfRepairGuides(
                            selfRepairGuidesAttachedToProduct
                        )
                    )}
                </div>

                {selfRepairGuidesOther.length > 0 &&
                    showOtherAvailableDocuments && (
                        <>
                            <h3 className="mt-6">
                                <Trans i18nKey="orderEntry.selfRepairGuides.other.title">
                                    Other Available Documents
                                </Trans>
                            </h3>
                            <div className={"grid grid-cols-12 gap-6"}>
                                {renderSelfRepairGuides(selfRepairGuidesOther)}
                            </div>
                        </>
                    )}
            </div>
        );
    },
    function areEqual(prevProps, nextProps) {
        return JSON.stringify(prevProps) === JSON.stringify(nextProps);
    }
);
